import { Button, Spin } from "antd";
import { ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";

import Modal from "@/components/Modal";

import {
  type EmployeeRoleAssignTarget,
  useEmployeeRoleAssignModal,
} from "../../hooks/useEmployeeRoleAssignModal";
import { RoleTransferPanel } from "./RoleTransferPanel";

export interface EmployeeRoleAssignModalProps {
  /** 是否打开 */
  open: boolean;
  /** 当前员工上下文 */
  target: EmployeeRoleAssignTarget | null;
  /** 关闭弹窗 */
  onClose: () => void;
  /** 保存成功后刷新列表 */
  onSuccess?: () => void;
}

/**
 * 员工角色分配弹窗：左右穿梭调整本店角色并保存
 */
export function EmployeeRoleAssignModal({
  open,
  target,
  onClose,
  onSuccess,
}: EmployeeRoleAssignModalProps) {
  const {
    loading,
    saving,
    displayName,
    isDirty,
    availableRoles,
    assignedRoles,
    filteredAvailableRoles,
    filteredAssignedRoles,
    selectedLeftKeys,
    selectedRightKeys,
    leftKeyword,
    rightKeyword,
    setLeftKeyword,
    setRightKeyword,
    toggleSelection,
    toggleSelectAll,
    moveToAssigned,
    moveToAvailable,
    handleSave,
    requestClose,
  } = useEmployeeRoleAssignModal({ open, target, onClose, onSuccess });

  return (
    <Modal
      open={open}
      title={
        <span className="inline-flex items-center gap-2">
          角色配置
          {isDirty ? (
            <span className="inline-flex items-center h-5 px-2 rounded-full text-[11px] font-medium bg-amber-50 text-amber-600">
              未保存
            </span>
          ) : null}
        </span>
      }
      titleIcon={<ShieldCheck className="w-5 h-5 text-orange-500" />}
      subtitle={
        <>
          当前员工：
          <span className="font-semibold text-slate-700">{displayName}</span>
          {" · "}已分配 <span className="font-medium text-orange-600">{assignedRoles.length}</span>{" "}
          个角色
        </>
      }
      width={760}
      onCancel={requestClose}
      contentClassName="!overflow-hidden flex flex-col min-h-105"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button onClick={requestClose}>取消</Button>
          <Button
            type="primary"
            disabled={!isDirty}
            loading={saving}
            onClick={() => void handleSave()}
          >
            保存
          </Button>
        </div>
      }
    >
      {/* Spin 默认会打断 flex 高度链，需把 min-h-0 / overflow 传到 ant-spin-container，列表才能滚动 */}
      <Spin
        spinning={loading}
        wrapperClassName="flex flex-1 min-h-0 flex-col overflow-hidden [&>.ant-spin-container]:flex [&>.ant-spin-container]:flex-1 [&>.ant-spin-container]:min-h-0 [&>.ant-spin-container]:flex-col [&>.ant-spin-container]:overflow-hidden"
      >
        <div className="flex h-full flex-1 flex-col gap-3 min-h-0 overflow-hidden">
          <p className="m-0 shrink-0 text-xs text-slate-400">
            左侧选择后点击箭头添加，修改后请点击保存生效
          </p>
          <div className="flex flex-1 items-stretch gap-3 min-h-0 overflow-hidden">
            <RoleTransferPanel
              title="可分配角色"
              count={availableRoles.length}
              items={filteredAvailableRoles}
              selectedKeys={selectedLeftKeys}
              keyword={leftKeyword}
              searchPlaceholder="搜索角色名称"
              emptyText="暂无可分配角色"
              onKeywordChange={setLeftKeyword}
              onToggle={(roleName, checked) => toggleSelection(roleName, checked, "left")}
              onToggleAll={(checked, items) => toggleSelectAll(checked, items, "left")}
            />
            <div className="flex flex-col items-center justify-center gap-2 shrink-0 w-12.5">
              <button
                type="button"
                className="inline-flex items-center justify-center w-12.5 h-9 rounded-md border border-orange-200 bg-orange-50 text-orange-600 cursor-pointer transition-colors hover:border-orange-300 hover:bg-orange-100 disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={moveToAssigned}
                disabled={!selectedLeftKeys.length}
                title="添加选中角色"
                aria-label="添加选中角色"
              >
                <ChevronRight className="w-4 h-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center w-12.5 h-9 rounded-md border border-orange-200 bg-orange-50 text-orange-600 cursor-pointer transition-colors hover:border-orange-300 hover:bg-orange-100 disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={moveToAvailable}
                disabled={!selectedRightKeys.length}
                title="移除选中角色"
                aria-label="移除选中角色"
              >
                <ChevronLeft className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
            <RoleTransferPanel
              title="已分配角色"
              count={assignedRoles.length}
              items={filteredAssignedRoles}
              selectedKeys={selectedRightKeys}
              keyword={rightKeyword}
              searchPlaceholder="搜索已分配角色"
              emptyText="暂未分配角色"
              onKeywordChange={setRightKeyword}
              onToggle={(roleName, checked) => toggleSelection(roleName, checked, "right")}
              onToggleAll={(checked, items) => toggleSelectAll(checked, items, "right")}
            />
          </div>
        </div>
      </Spin>
    </Modal>
  );
}
